import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, CheckCircle2 } from 'lucide-react';

// Palette rule (Bloomberg × Stripe):
//   Blue = trust / verification (primary brand)
//   Grey = neutral / low
//   Green = success ONLY (payment received, payout paid, dispute resolved)
//   Gold = premium
//   Black = enterprise / authority

export const TrustBadge = ({ score, size = 'md' }) => {
  const sizeClasses = size === 'sm' ? 'text-xs px-2 py-0.5' : 'text-sm px-2.5 py-1';
  // No red / amber / green for score — that was traffic-light. Use blue-scale.
  let color = 'bg-gray-50 text-gray-700 border-gray-200';
  if (score >= 80) color = 'bg-[#0055FF]/10 text-[#0044CC] border-[#0055FF]/30';
  else if (score >= 60) color = 'bg-blue-50 text-blue-800 border-blue-200';

  return (
    <span className={`inline-flex items-center gap-1 font-mono font-medium border rounded ${sizeClasses} ${color}`} data-testid="trust-badge">
      <Shield className="w-3 h-3" strokeWidth={2.5} />
      {score}/100
    </span>
  );
};

export const VerificationBadge = ({ level }) => {
  const labels = {
    'unverified':       { label: 'Unverified',       color: 'bg-gray-100 text-gray-700 border-gray-200' },
    'email_verified':   { label: 'Email Verified',   color: 'bg-blue-50 text-blue-800 border-blue-200' },
    'phone_verified':   { label: 'Phone Verified',   color: 'bg-blue-50 text-blue-800 border-blue-200' },
    'sms_verified':     { label: 'SMS Verified',     color: 'bg-blue-50 text-blue-800 border-blue-200' },
    'identity':         { label: 'Identity Verified',color: 'bg-blue-50 text-[#0044CC] border-blue-200' },
    'business':         { label: 'Business Verified',color: 'bg-[#0055FF]/10 text-[#0044CC] border-[#0055FF]/30' },
    'premium':          { label: 'Premium',          color: 'bg-[#FDF6E3] text-[#8B6914] border-[#D4AF37]' },
    'enterprise':       { label: 'Enterprise',       color: 'bg-gray-900 text-white border-gray-900' },
    'verified':         { label: 'Fully Verified',   color: 'bg-[#0055FF]/10 text-[#0044CC] border-[#0055FF]/30' },
  };
  const config = labels[level?.toLowerCase()?.replace(/[- ]/g, '_')] || { label: level, color: 'bg-blue-50 text-blue-700 border-blue-200' };

  return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded border ${config.color}`} data-testid="verification-badge">
      <CheckCircle2 className="w-3 h-3" strokeWidth={2.5} />
      {config.label}
    </span>
  );
};

export const AliasBadge = ({ alias, clickable = false, userId = null }) => {
  const navigate = useNavigate();
  const isAdmin = alias?.startsWith('LX-ADMIN');
  const isBuyer = alias?.startsWith('LX-B');
  const color = isAdmin ? 'bg-gray-900 text-white border-gray-900'
    : isBuyer ? 'bg-blue-50 text-[#0044CC] border-blue-200'
    : 'bg-gray-50 text-gray-800 border-gray-300';

  const baseClasses = `inline-flex items-center font-mono text-xs font-semibold px-2 py-0.5 border rounded ${color}`;
  const clickableClasses = clickable ? 'cursor-pointer hover:opacity-80 transition-opacity' : '';

  const handleClick = (e) => {
    if (clickable && userId) {
      e.preventDefault();
      e.stopPropagation();
      navigate(`/admin/users/${userId}`);
    }
  };

  return (
    <span 
      className={`${baseClasses} ${clickableClasses}`}
      onClick={handleClick}
      data-testid={`alias-badge-${alias}`}
      title={clickable ? 'Click to view user profile' : undefined}
    >
      {alias}
    </span>
  );
};

export const StatusBadge = ({ status }) => {
  // Green reserved for successful outcomes only (paid, resolved).
  const config = {
    'available': { label: 'Available', color: 'bg-blue-50 text-[#0044CC] border-blue-200', dot: 'bg-[#0055FF]' },
    'sold':      { label: 'Sold',      color: 'bg-gray-50 text-gray-700 border-gray-200',  dot: 'bg-gray-400' },
    'pending':   { label: 'Pending',   color: 'bg-gray-50 text-gray-700 border-gray-200',  dot: 'bg-gray-400' },
    'paid':      { label: 'Paid',      color: 'bg-green-50 text-green-800 border-green-200', dot: 'bg-green-500' },   // SUCCESS
    'refunded':  { label: 'Refunded',  color: 'bg-gray-100 text-gray-700 border-gray-300', dot: 'bg-gray-500' },
    'active':    { label: 'Active',    color: 'bg-blue-50 text-blue-800 border-blue-200',  dot: 'bg-[#0055FF]' },
    'open':      { label: 'Open',      color: 'bg-gray-50 text-gray-700 border-gray-200',  dot: 'bg-gray-400' },
    'resolved':  { label: 'Resolved',  color: 'bg-green-50 text-green-800 border-green-200', dot: 'bg-green-500' },   // SUCCESS
    'approved':  { label: 'Approved',  color: 'bg-green-50 text-green-800 border-green-200', dot: 'bg-green-500' },   // SUCCESS
    'rejected':  { label: 'Rejected',  color: 'bg-red-50 text-red-700 border-red-200',     dot: 'bg-red-500' },       // FAILURE only
  }[status?.toLowerCase()] || { label: status, color: 'bg-gray-50 text-gray-700 border-gray-200', dot: 'bg-gray-400' };

  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 border rounded ${config.color}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${config.dot}`} />
      {config.label}
    </span>
  );
};

export const FreshnessBadge = ({ hours }) => {
  // Muted labels, not traffic-light colours.
  let cfg;
  if (hours <= 1)        cfg = { label: 'New',    dot: 'bg-[#0055FF]', color: 'bg-blue-50 text-[#0044CC] border-blue-200' };
  else if (hours <= 24)  cfg = { label: 'Fresh',  dot: 'bg-[#0055FF]', color: 'bg-blue-50 text-blue-800 border-blue-200' };
  else if (hours <= 168) cfg = { label: 'Recent', dot: 'bg-gray-500',  color: 'bg-gray-50 text-gray-700 border-gray-200' };
  else                   cfg = { label: 'Archive',dot: 'bg-gray-400',  color: 'bg-gray-50 text-gray-600 border-gray-200' };
  const timeLabel = hours < 24 ? `${hours}h` : `${Math.floor(hours / 24)}d`;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 border rounded ${cfg.color}`} data-testid="freshness-badge">
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label} · {timeLabel}
    </span>
  );
};

// Exclusivity badge — one of the most prominent pieces of info on every lead card.
export const ExclusivityBadge = ({ exclusivity, sharedCap, size = 'md' }) => {
  const sizeClasses = size === 'sm' ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1';
  const cfg = {
    'exclusive': { label: 'Exclusive',   color: 'bg-[#0055FF] text-white border-[#0055FF]' },        // strong blue
    'shared':    { label: `Shared${sharedCap ? ` · max ${sharedCap}` : ''}`, color: 'bg-gray-100 text-gray-800 border-gray-300' },
    'multi_buy': { label: 'Multi-buy',   color: 'bg-white text-gray-700 border-gray-400' },
  }[exclusivity] || { label: exclusivity || 'Unknown', color: 'bg-gray-100 text-gray-700 border-gray-200' };
  return (
    <span className={`inline-flex items-center gap-1 font-medium border rounded ${sizeClasses} ${cfg.color}`} data-testid={`exclusivity-badge-${exclusivity}`}>
      {cfg.label}
    </span>
  );
};

// Marketplace Standard tier badge — Bronze → Diamond
export const MarketplaceStandardBadge = ({ tier, label, size = 'md' }) => {
  if (!tier) return (
    <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded border bg-gray-50 text-gray-500 border-gray-200`} data-testid="marketplace-standard-none">
      Unranked
    </span>
  );
  const sizeClasses = size === 'sm' ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1';
  const cfg = {
    'bronze':   { color: 'bg-[#F5E4D0] text-[#7A4A1C] border-[#D0904D]' },
    'silver':   { color: 'bg-[#EEF1F5] text-[#4A5568] border-[#A0AEC0]' },
    'gold':     { color: 'bg-[#FDF6E3] text-[#8B6914] border-[#D4AF37]' },
    'platinum': { color: 'bg-[#F8FAFC] text-[#374151] border-[#94A3B8]' },
    'diamond':  { color: 'bg-gray-900 text-white border-gray-900' },
  }[tier] || { color: 'bg-gray-50 text-gray-700 border-gray-200' };
  return (
    <span className={`inline-flex items-center gap-1 font-semibold uppercase tracking-wider border rounded ${sizeClasses} ${cfg.color}`} data-testid={`marketplace-standard-${tier}`}>
      ★ {label || tier}
    </span>
  );
};

// Quality Score chip — deterministic score (0-100), blue palette
export const QualityScoreBadge = ({ score, size = 'md' }) => {
  const sizeClasses = size === 'sm' ? 'text-[10px] px-1.5 py-0.5' : 'text-xs px-2 py-1';
  let color = 'bg-gray-50 text-gray-700 border-gray-200';
  if (score >= 85) color = 'bg-[#0055FF] text-white border-[#0055FF]';
  else if (score >= 70) color = 'bg-blue-50 text-[#0044CC] border-blue-200';
  else if (score >= 50) color = 'bg-gray-100 text-gray-800 border-gray-300';
  return (
    <span className={`inline-flex items-center gap-1 font-mono font-medium border rounded ${sizeClasses} ${color}`} data-testid="quality-score-badge">
      Q {score}/100
    </span>
  );
};
